// Shared messaging definitions; values and helpers are maintained here.
import { CHROME, STATUS } from './palette';
import { IN_FLIGHT_COLOR } from './protocols';


// --- Simulated nodes / message send (phase 5) ------------------------------
// THE HARD CONSTRAINT this whole phase is built under:
// nothing here ever transmits to the real mesh. The backend config schema
// deliberately has no flag that would permit live transmit — there is
// nothing for a frontend control to turn on, and none is built. Every send
// in this UI goes out with transport:"simulated", full stop; the plain-
// English copy below is what the compose surface shows at the point of
// sending, and GET /api/transmit/status (surfaced on the same page) is what
// backs up "why can't I send for real" in the backend's own words.
export const SIMULATED_SEND_NOTICE = "This message goes to a simulated peer only — it will not reach the radio.";


export function liveTransmitDisabledNotice(reason: string): string {
  return `Live transmit is off on this instance: ${reason}. Every send here is simulated only.`;
}


// Delivery states, in the fixed order the backend contract specifies:
// queued -> fragmenting -> sent_to_transport -> acked, with failed/expired
// as terminal alternatives reachable from any earlier state. Colours reuse
// the existing STATUS/CHROME/IN_FLIGHT vocabulary rather than inventing a
// new palette: acked borrows STATUS.good (the one place a message's own
// journey genuinely does succeed), sent_to_transport borrows
// IN_FLIGHT_COLOR (the map's own "something is in flight right now"
// colour — the same concept applies here), failed/expired split
// STATUS.critical/STATUS.serious so the two terminal failure modes stay
// visually distinct from each other.
export type MessageState = "queued" | "fragmenting" | "sent_to_transport" | "acked" | "failed" | "expired";


// The in-progress happy-path order (for a step/progress display); failed
// and expired are terminal alternatives from any of these, not steps in
// the same line.
export const MESSAGE_PROGRESS_ORDER: MessageState[] = ["queued", "fragmenting", "sent_to_transport", "acked"];


export const MESSAGE_STATE_ORDER: MessageState[] = ["queued", "fragmenting", "sent_to_transport", "acked", "failed", "expired"];


export interface MessageStateInfo {
  label: string;
  color: { light: string; dark: string };
}


export const MESSAGE_STATE_INFO: Record<MessageState, MessageStateInfo> = {
  queued: { label: "Queued", color: CHROME.inkMuted },
  fragmenting: { label: "Fragmenting", color: STATUS.warning },
  sent_to_transport: { label: "Sent to transport", color: { light: IN_FLIGHT_COLOR, dark: IN_FLIGHT_COLOR } },
  acked: { label: "Acknowledged", color: STATUS.good },
  failed: { label: "Failed", color: STATUS.critical },
  expired: { label: "Expired", color: STATUS.serious },
};


export function isMessageState(x: string | null | undefined): x is MessageState {
  return x === "queued" || x === "fragmenting" || x === "sent_to_transport" || x === "acked" || x === "failed" || x === "expired";
}


export function messageStateInfo(raw: string | null | undefined): MessageStateInfo {
  return isMessageState(raw) ? MESSAGE_STATE_INFO[raw] : { label: raw || "Unknown", color: CHROME.inkMuted };
}


// Ordering tiebreak for AppData's message upsert (see its own comment for
// the real race this exists to close: the simulated transport can run the
// whole queued->acked state machine in ~3ms, faster than the
// POST /api/messages/send promise that kicked it off resolves — so a
// caller's own optimistic update, built from that POST response, can
// arrive AFTER the WS has already advanced the row past it). Rank, not
// state_history length, is only the TIEBREAK; length is the primary
// signal there. failed/expired rank above every in-progress step since
// they're terminal from wherever they happen — never superseded once
// reached, same as acked.
export function messageStateRank(raw: string | null | undefined): number {
  if (!isMessageState(raw)) return -1;
  const idx = MESSAGE_PROGRESS_ORDER.indexOf(raw);
  return idx >= 0 ? idx : MESSAGE_PROGRESS_ORDER.length;
}
